import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { 
  Subscription, 
  SubscriptionTier, 
  SubscriptionStatus, 
  SubscriptionPlatform,
  SUBSCRIPTION_LIMITS,
  PurchaseResult 
} from '../types/subscription';
import { Alert, InteractionManager, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { addIOSSubscriptionPurchaseListener } from 'goalmint-subscription-store';
import { subscriptionService } from '../services/subscriptionService';
import { revenueCatService } from '../services/revenueCatService';
import { isRevenueCatConfigured, isRevenueCatWebConfigured } from '../config/revenuecat';
import {
  getSubscriptionDoc,
  setSubscriptionDoc,
  watchSubscriptionDoc,
} from '../utils/subscriptionFirestore';

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  currentTier: SubscriptionTier;
  canCreateGoal: (currentGoalCount: number) => boolean;
  shouldShowAds: () => boolean;
  getMaxGoals: () => number;
  getFeatures: () => string[];
  canExportData: () => boolean;
  getExportFormats: () => ('csv' | 'excel' | 'pdf')[];
  canShowDashboardCard: (cardName: string) => boolean;
  updateSubscription: (subscription: Subscription) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  // Developer mode functions
  isDeveloperMode: boolean;
  setDeveloperMode: (enabled: boolean) => void;
  setTestTier: (tier: SubscriptionTier) => Promise<void>;
  setTestTrial: (tier: SubscriptionTier) => Promise<void>;
  resetToFree: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Default subscription for new users
const createDefaultSubscription = (userId: string): Subscription => ({
  userId,
  tier: 'free',
  status: 'active',
  platform: 'web', // Will be updated based on actual platform
  startDate: new Date().toISOString(),
  autoRenew: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  const [localTestTier, setLocalTestTier] = useState<SubscriptionTier>('free');

  // Get current tier from subscription, local test tier, or default to free
  const currentTier = isDeveloperMode ? localTestTier : (subscription?.tier || 'free');

  // Check if user can create a new goal
  const canCreateGoal = useCallback((currentGoalCount: number): boolean => {
    const limits = SUBSCRIPTION_LIMITS[currentTier];
    return currentGoalCount < limits.maxGoals;
  }, [currentTier]);

  // Check if ads should be shown
  const shouldShowAds = useCallback((): boolean => {
    if (isDeveloperMode) {
      return SUBSCRIPTION_LIMITS[currentTier].showAds;
    }
    if (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) {
      return true; // Show ads for inactive subscriptions
    }
    return SUBSCRIPTION_LIMITS[currentTier].showAds;
  }, [subscription, currentTier, isDeveloperMode]);

  // Check if subscription is active (including trial)
  const isSubscriptionActive = useCallback((): boolean => {
    if (isDeveloperMode) return true;
    if (!subscription) return false;
    
    // Check if subscription is active or in trial
    if (subscription.status === 'active' || subscription.status === 'trial') {
      // Check if subscription hasn't expired
      if (subscription.endDate) {
        return new Date(subscription.endDate) > new Date();
      }
      return true;
    }
    
    return false;
  }, [subscription, isDeveloperMode]);

  // Get maximum goals for current tier
  const getMaxGoals = useCallback((): number => {
    return SUBSCRIPTION_LIMITS[currentTier].maxGoals;
  }, [currentTier]);

  // Get features for current tier
  const getFeatures = useCallback((): string[] => {
    return SUBSCRIPTION_LIMITS[currentTier].features;
  }, [currentTier]);

  // Check if user can export data
  const canExportData = useCallback((): boolean => {
    return SUBSCRIPTION_LIMITS[currentTier].canExportData;
  }, [currentTier]);

  // Get available export formats for current tier
  const getExportFormats = useCallback((): ('csv' | 'excel' | 'pdf')[] => {
    return SUBSCRIPTION_LIMITS[currentTier].exportFormats;
  }, [currentTier]);

  // Check if user can see specific dashboard card
  const canShowDashboardCard = useCallback((cardName: string): boolean => {
    return SUBSCRIPTION_LIMITS[currentTier].dashboardCards.includes(cardName);
  }, [currentTier]);

  // Update subscription in Firestore
  const updateSubscription = useCallback(async (updatedSubscription: Subscription) => {
    try {
      if (!user || !isAuthenticated) {
        throw new Error('No authenticated user');
      }

      await setSubscriptionDoc(user.uid, {
        ...updatedSubscription,
        updatedAt: new Date().toISOString(),
      });

      setSubscription(updatedSubscription);
    } catch (error) {
      console.error('Error updating subscription:', error);
      setError(error instanceof Error ? error.message : 'Failed to update subscription');
      throw error;
    }
  }, [user, isAuthenticated]);

  // Developer mode: Set test tier (local storage only)
  const setTestTier = useCallback(async (tier: SubscriptionTier) => {
    try {
      if (!user || !isAuthenticated) {
        Alert.alert('Not signed in', 'You must be signed in to set the test tier.');
        throw new Error('No authenticated user');
      }
      // Map Platform.OS to SubscriptionPlatform type
      let platform: SubscriptionPlatform = 'web';
      if (Platform.OS === 'ios') platform = 'ios';
      else if (Platform.OS === 'android') platform = 'android';
      // fallback to 'web' for others
      const testSubscription: Subscription = {
        userId: user.uid,
        tier,
        status: 'active',
        platform,
        startDate: new Date().toISOString(),
        autoRenew: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      if (Platform.OS === 'web') {
        localStorage.setItem('goalmint_dev_subscription', JSON.stringify(testSubscription));
        localStorage.setItem('goalmint_dev_tier', tier);
      } else {
        await AsyncStorage.setItem('goalmint_dev_subscription', JSON.stringify(testSubscription));
        await AsyncStorage.setItem('goalmint_dev_tier', tier);
      }
      setLocalTestTier(tier);
      setSubscription(testSubscription);
    } catch (error) {
      console.error('Error setting test tier:', error);
      Alert.alert('Error', 'Failed to set test tier');
      throw error;
    }
  }, [user, isAuthenticated]);

  // Developer mode: Test trial subscription
  const setTestTrial = useCallback(async (tier: SubscriptionTier) => {
    try {
      if (!user || !isAuthenticated) {
        Alert.alert('Not signed in', 'You must be signed in to set the test trial.');
        throw new Error('No authenticated user');
      }
      
      // Create trial subscription (7 days from now)
      const trialEndDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      const testTrialSubscription: Subscription = {
        userId: user.uid,
        tier,
        status: 'trial',
        platform: 'web',
        startDate: new Date().toISOString(),
        endDate: trialEndDate,
        trialEndDate,
        autoRenew: true,
        lastBillingDate: new Date().toISOString(),
        nextBillingDate: trialEndDate,
        productId: `${tier}_monthly`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      if (Platform.OS === 'web') {
        localStorage.setItem('goalmint_dev_subscription', JSON.stringify(testTrialSubscription));
        localStorage.setItem('goalmint_dev_tier', tier);
      } else {
        await AsyncStorage.setItem('goalmint_dev_subscription', JSON.stringify(testTrialSubscription));
        await AsyncStorage.setItem('goalmint_dev_tier', tier);
      }
      setLocalTestTier(tier);
      setSubscription(testTrialSubscription);
      
      Alert.alert('Trial Started', `7-day trial for ${tier} plan activated!`);
    } catch (error) {
      console.error('Error setting test trial:', error);
      Alert.alert('Error', 'Failed to set test trial');
      throw error;
    }
  }, [user, isAuthenticated]);

  // Developer mode: Reset to free
  const resetToFree = useCallback(async () => {
    try {
      await setTestTier('free');
    } catch (error) {
      console.error('Error resetting to free:', error);
    }
  }, [setTestTier]);

  // Developer mode: Toggle developer mode
  const setDeveloperMode = useCallback(async (enabled: boolean) => {
    setIsDeveloperMode(enabled);
    if (enabled) {
      let savedTier: SubscriptionTier | null = null;
      if (Platform.OS === 'web') {
        savedTier = localStorage.getItem('goalmint_dev_tier') as SubscriptionTier;
      } else {
        savedTier = (await AsyncStorage.getItem('goalmint_dev_tier')) as SubscriptionTier;
      }
      if (savedTier && ['free', 'standard', 'premium'].includes(savedTier)) {
        setLocalTestTier(savedTier);
      }
      Alert.alert('Developer Mode', 'Developer mode enabled. You can now test different subscription tiers.');
    } else {
      if (Platform.OS === 'web') {
        localStorage.removeItem('goalmint_dev_subscription');
        localStorage.removeItem('goalmint_dev_tier');
      } else {
        await AsyncStorage.removeItem('goalmint_dev_subscription');
        await AsyncStorage.removeItem('goalmint_dev_tier');
      }
      setLocalTestTier('free');
    }
  }, []);

  // Initialize RevenueCat and sync store entitlements when user signs in
  useEffect(() => {
    const revenueCatEnabled =
      Platform.OS === 'web'
        ? isRevenueCatWebConfigured()
        : isRevenueCatConfigured();

    if (!user || !isAuthenticated || isDeveloperMode) {
      if (!user && revenueCatEnabled && Platform.OS !== 'web') {
        revenueCatService.logOut().catch(console.warn);
      }
      return;
    }

    if (!revenueCatEnabled) {
      return;
    }

    let removeListener: (() => void) | undefined;

    const task = InteractionManager.runAfterInteractions(() => {
      (async () => {
        try {
          await subscriptionService.ensureRevenueCatReady(user.uid);

          if (Platform.OS !== 'web') {
            removeListener = revenueCatService.onCustomerInfoUpdated(async (customerInfo) => {
              try {
                const synced = await subscriptionService.applyNativeCustomerInfo(
                  user.uid,
                  customerInfo
                );
                if (synced) {
                  setSubscription(synced);
                }
              } catch (error) {
                console.warn('RevenueCat customer info sync failed:', error);
              }
            });
          }

          const synced = await subscriptionService.syncSubscriptionFromStoreWithRetry(user.uid);
          if (synced) {
            setSubscription(synced);
          }
        } catch (error) {
          console.warn('RevenueCat sync failed:', error);
        }
      })();
    });
    return () => {
      task.cancel();
      removeListener?.();
    };
  }, [user, isAuthenticated, isDeveloperMode]);

  // Sync immediately when StoreKit completes a SubscriptionStoreView purchase.
  useEffect(() => {
    if (Platform.OS !== 'ios' || !user || !isAuthenticated || isDeveloperMode) {
      return;
    }

    const remove = addIOSSubscriptionPurchaseListener(() => {
      subscriptionService.syncSubscriptionFromStoreWithRetry(user.uid).then((synced) => {
        if (synced) {
          setSubscription(synced);
        }
      });
    });

    return remove;
  }, [user, isAuthenticated, isDeveloperMode]);

  // Refresh subscription from Firestore
  const refreshSubscription = useCallback(async () => {
    try {
      if (!user || !isAuthenticated) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }
      if (isDeveloperMode) {
        let savedSubscription: string | null = null;
        if (Platform.OS === 'web') {
          savedSubscription = localStorage.getItem('goalmint_dev_subscription');
        } else {
          savedSubscription = await AsyncStorage.getItem('goalmint_dev_subscription');
        }
        if (savedSubscription) {
          setSubscription(JSON.parse(savedSubscription));
        }
        setIsLoading(false);
        return;
      }

      const existing = await getSubscriptionDoc(user.uid);
      if (existing) {
        setSubscription(existing);
      } else {
        const defaultSubscription = createDefaultSubscription(user.uid);
        try {
          await setSubscriptionDoc(user.uid, defaultSubscription);
        } catch (error) {
          console.warn('Could not save default subscription to Firestore:', error);
        }
        setSubscription(defaultSubscription);
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setError(error instanceof Error ? error.message : 'Failed to load subscription');
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated, isDeveloperMode]);

  // Set up subscription listener
  useEffect(() => {
    if (!user || !isAuthenticated) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    if (isDeveloperMode) {
      (async () => {
        let savedSubscription: string | null = null;
        if (Platform.OS === 'web') {
          savedSubscription = localStorage.getItem('goalmint_dev_subscription');
        } else {
          savedSubscription = await AsyncStorage.getItem('goalmint_dev_subscription');
        }
        if (savedSubscription) {
          setSubscription(JSON.parse(savedSubscription));
        } else {
          const defaultSubscription = createDefaultSubscription(user.uid);
          setSubscription(defaultSubscription);
        }
        setIsLoading(false);
      })();
      return;
    }

    const unsubscribe = watchSubscriptionDoc(
      user.uid,
      (data, exists) => {
        if (exists && data) {
          setSubscription(data);
        } else {
          const defaultSubscription = createDefaultSubscription(user.uid);
          setSubscriptionDoc(user.uid, defaultSubscription).catch(console.error);
          setSubscription(defaultSubscription);
        }
        setIsLoading(false);
      },
      (error: Error) => {
        console.error('Error in subscription listener:', error);
        setError('Failed to load subscription data');
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user, isAuthenticated, isDeveloperMode]);

  const value: SubscriptionContextType = {
    subscription,
    isLoading,
    error,
    currentTier,
    canCreateGoal,
    shouldShowAds,
    getMaxGoals,
    getFeatures,
    canExportData,
    getExportFormats,
    canShowDashboardCard,
    updateSubscription,
    refreshSubscription,
    isDeveloperMode,
    setDeveloperMode,
    setTestTier,
    setTestTrial,
    resetToFree
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 